import { Box, InlineLink, PrimaryButton, SecondaryButton, Typography } from "@husqvarna/ui-core";
import "./ProposalFinancingEquipmentOverview.css";
import { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useForm } from "react-hook-form";
import { Proposal } from "../../models/Proposal";
import { ProposalProduct } from "../../models/ProposalProduct";
import FormTextWithSuffix from "../../../shared/components/FormTextWithSuffix";
import { useSaveEquipmentPricesMutation } from "../../projectApi";
import { getEffectiveUnitPrice, getProposalProductsTotal } from "../../../shared/utils/proposalPriceUtils";
import TextButtonWithIcon from "../../../shared/components/TextButtonWithIcon";
import { ReactComponent as ReturnIcon } from "../../../shared/icons/return.svg";

export type EquipmentOverviewProps = {
  proposal: Proposal;
  projectId?: string;
  proposalId?: string;
  countryCode?: string;
  currency?: string;
};

interface EquipmentPriceFormValues {
  prices: {
    proposalProductId: string;
    price: string;
  }[];
}

const getDisplayPrice = (equipment: ProposalProduct) =>
  getEffectiveUnitPrice(equipment) * equipment.quantity;

const getCatalogTotal = (equipment: ProposalProduct) =>
  equipment.product.price.valueVatExcl * equipment.quantity;

export const ProposalFinancingEquipmentOverview = ({
  proposal,
  projectId,
  proposalId,
  countryCode,
  currency,
}: EquipmentOverviewProps) => {
  const intl = useIntl();
  const isUS = countryCode === "US";
  const [isEditing, setIsEditing] = useState(false);
  const firstInputRef = useRef<HTMLInputElement | null>(null);
  const equipments = proposal.mowers?.concat(proposal.referenceStations ?? []) ?? [];

  const totalQuantity =
    equipments?.reduce((acc, product) => acc + product.quantity, 0) ?? 0;

  const toFixedPrice = (value: number) =>
    parseFloat(value.toFixed(2)).toString();

  const buildDefaultPrices = () =>
    equipments?.map((eq) => ({
      proposalProductId: eq.id,
      price: toFixedPrice(getDisplayPrice(eq)),
    })) ?? [];

  const buildCatalogPrices = () =>
    equipments?.map((eq) => ({
      proposalProductId: eq.id,
      price: toFixedPrice(getCatalogTotal(eq)),
    })) ?? [];

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm<EquipmentPriceFormValues>({
    defaultValues: { prices: buildDefaultPrices() },
  });

  const [saveEquipmentPrices, { isLoading: isSaving }] =
    useSaveEquipmentPricesMutation();

  useEffect(() => {
    reset({ prices: buildDefaultPrices() });
  }, [proposal]);

  useEffect(() => {
    if (isEditing) {
      requestAnimationFrame(() => firstInputRef.current?.focus());
    }
  }, [isEditing]);

  useEffect(() => {
    if (!(isEditing && isDirty)) return;
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isEditing, isDirty]);

  const onSave = handleSubmit(async (data) => {
    if (!projectId || !proposalId) return;
    try {
      await saveEquipmentPrices({
        projectId,
        proposalId,
        prices: data.prices.map((p) => {
          const eq = equipments?.find((e) => e.id === p.proposalProductId);
          const catalogTotal = eq ? parseFloat(getCatalogTotal(eq).toFixed(2)) : null;
          const enteredPrice = parseFloat(Number(p.price).toFixed(2));
          const unitPrice = eq ? enteredPrice / eq.quantity : enteredPrice;
          return {
            proposalProductId: p.proposalProductId,
            price: enteredPrice === catalogTotal ? null : unitPrice,
          };
        }),
      }).unwrap();
      setIsEditing(false);
    } catch {
      // Error handled by handleSavedState in projectApi
    }
  });

  const onCancel = () => {
    reset({ prices: buildDefaultPrices() });
    setIsEditing(false);
  };

  const onRevert = () => {
    reset({ prices: buildCatalogPrices() });
  };

  return (
    <Box
    height={"full"}
      className={
        isEditing
          ? "equipment-overview--editing"
          : undefined
      }
    >
      <Box mt={0} container flexDirection="column" minHeightZero>
        {/* Header */}
        <Box
          flexDirection="row"
          alignItems="flex-start"
          justifyContent="space-between"
          mb={6}
        >
          <Box flexDirection="row" alignItems="center" gap={2}>
            <Typography
              variant="Body2Medium"
              tag="h3"
              className="tcsp-medium"
              color="Grey600"
              text={intl.formatMessage({
                id: "project.proposal.financing.equipmentoverview",
                defaultMessage: "Equipment overview",
              })}
            />
            <Typography color="Grey500" variant="Body2">
              ({totalQuantity})
            </Typography>
          </Box>
          {isUS && !isEditing && (
            <InlineLink mt={-1} variant="Body2" mr={-3} text={intl.formatMessage({
                  id: "project.proposal.financing.editPrice",
                  defaultMessage: "Edit price",
                })} onClick={() => setIsEditing(true)}
            />
          )}
        </Box>

        {/* View mode */}
        {!isEditing &&
          equipments?.map((equipment) => (
            <Box
              key={equipment.id}
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              gap={3}
              mb={4}
            >
              <Typography
                tag="p"
                color="Grey600"
                variant="Body2"
                text={equipment.quantity + "x " + equipment.product.name}
              />
              <Typography
                tag="p"
                color="Grey600"
                variant="Body2"
                text={intl.formatNumber(getDisplayPrice(equipment), {
                  currency: equipment.product.price.currency,
                  currencyDisplay: "code",
                  maximumFractionDigits: 0,
                  style: "currency",
                })}
              />
            </Box>
          ))}

        {/* Edit mode */}
        {isEditing && (
          <div>
            {equipments?.map((equipment, index) => (
              <Box
                key={equipment.id}
                flexDirection="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                mb={4}
              >
                <Typography tag="p" color="Grey600" variant="Body2">
                  {equipment.quantity + "x  " + equipment.product.name}
                </Typography>
                <Box width={40} flexShrink={0}>
                  <FormTextWithSuffix
                    className="tight"
                    {...register(`prices.${index}.price`)}
                    ref={(el: HTMLInputElement | null) => {
                      register(`prices.${index}.price`).ref(el);
                      if (index === 0) firstInputRef.current = el;
                    }}
                    value={watch(`prices.${index}.price`)}
                    placeholder={intl.formatMessage({id: 'general.amout', defaultMessage: 'Amount'})}
                    suffix={currency ?? equipment.product.price.currency}
                    allowDecimals={true}
                  />
                </Box>
                <input
                  type="hidden"
                  {...register(`prices.${index}.proposalProductId`)}
                />
              </Box>
            ))}

          </div>
        )}

        <Box flexDirection='row' className="border-top" mt={6} pt={6} justifyContent='space-between' alignContent='center'>
          <Typography variant="Body2" text={intl.formatMessage({id: 'project.proposal.financing.sumTotal', defaultMessage: 'Sum total'})} />
          <Typography tag="p">
            <Typography tag="span" variant="Body2Medium"
              text={intl.formatNumber(getProposalProductsTotal(equipments), {style: 'decimal', maximumFractionDigits: 0})}
            />
            <Typography tag="span" variant="Body2" text={` ${currency ?? currency}`} />
          </Typography>
          
        </Box>

        {isUS && isEditing && (
          <Box flexDirection='row' justifyContent='space-between' alignContent='center' gap={3} ml={-4} mt={6}>
              <TextButtonWithIcon
                icon={<ReturnIcon />}
                variant="ghost"
                onClick={onRevert}
                text={intl.formatMessage({
                  id: "project.proposal.financing.revertPrices",
                  defaultMessage: "Original price",
                })}
              />
            <Box flexDirection="row" justifyContent="flex-end" gap={3}>
              <SecondaryButton onClick={onCancel}
              className="small"
                text={intl.formatMessage({
                  id: "general.cancel",
                  defaultMessage: "Cancel",
                })}
                />
              <PrimaryButton onClick={onSave} disabled={isSaving}
              className="small"
                text={intl.formatMessage({
                  id: "project.proposal.financing.savePrices",
                  defaultMessage: "Save prices",
                })
              } />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};
